import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/session';
import { LegalLinks } from '../components/LegalLinks';
import { initials } from '../lib/format';
import { useApi } from '../lib/useApi';
import { useToast } from '../lib/toast';
import { enableWebPush, useWebPushState } from '../lib/webpush';
import { Label } from '../components/Label';
import { AREA_LABEL, type StaffArea } from '../types';
import type { AbrechnungResponse } from './abrechnung/types';
import { useOpenRegistrationsCount } from './accounts/useOpenRegistrationsCount';
import { useOffeneAnfragen } from './anfragen/OffeneAnfragen';
import { useChatClientsUnread } from './chat/useChatUnread';
import { useKanzlei } from './context';
import { Glocke } from './glocke/Glocke';

// Rahmen des Kanzlei-Dashboards: Kopfzeile mit Glocke, Seitenleiste mit Zählern, Rollen-Box.
export function KanzleiLayout({ children }: { children: ReactNode }) {
  const { me } = useKanzlei();
  const { pathname } = useLocation();
  const openRegs = useOpenRegistrationsCount();
  const chatClients = useChatClientsUnread();
  const anfragen = useOffeneAnfragen().data?.items.filter((r) => r.mine).length ?? 0;
  const user = me.user;
  // Abrechnung (Admin): Zähler = Mandanten mit Positionen „zu berechnen“
  const abrechnung = useApi<AbrechnungResponse>(user.isAdmin ? '/api/kanzlei/abrechnung' : null, {
    live: ['abrechnung', 'clients', 'invoices'],
  }).data?.open.length;
  // Posteingang (Admin): Briefe, die noch keinem Mandanten zugeordnet sind
  const post = useApi<{ n: number }>(user.isAdmin ? '/api/kanzlei/posteingang/anzahl' : null, { live: ['posteingang'] }).data?.n;
  // Postausgang (Admin): Briefe der Mandanten, die die Kanzlei noch drucken und verschicken muss
  const ausgang = useApi<{ n: number }>(user.isAdmin ? '/api/kanzlei/postausgang/anzahl' : null, { live: ['postausgang'] }).data?.n;

  // wide: nur in der Seitenleiste am Rechner, in der Reiterzeile (Tablet, Handy) ist kein Platz; dort im Konto-Menü
  const items: { to: string; label: string; short?: string; count: number; on: boolean; wide?: boolean }[] = [
    { to: '/kanzlei', label: 'Übersicht', count: chatClients, on: pathname === '/kanzlei' || pathname === '/kanzlei/' },
    {
      to: '/kanzlei/mandanten',
      label: 'Mandanten',
      count: user.isAdmin ? openRegs : 0,
      on: pathname.startsWith('/kanzlei/mandanten') || pathname.startsWith('/kanzlei/registrierungen'),
    },
    {
      to: '/kanzlei/anfragen',
      label: 'Offene Anfragen',
      short: 'Anfragen',
      count: anfragen,
      on: pathname.startsWith('/kanzlei/anfragen'),
    },
    ...(user.isAdmin
      ? [
          {
            to: '/kanzlei/posteingang',
            label: 'Posteingang',
            count: post ?? 0,
            on: pathname.startsWith('/kanzlei/posteingang'),
            wide: true,
          },
          {
            to: '/kanzlei/postausgang',
            label: 'Postausgang',
            count: ausgang ?? 0,
            on: pathname.startsWith('/kanzlei/postausgang'),
            wide: true,
          },
          {
            to: '/kanzlei/abrechnung',
            label: 'Abrechnung',
            count: abrechnung ?? 0,
            on: pathname.startsWith('/kanzlei/abrechnung'),
            wide: true,
          },
          { to: '/kanzlei/team', label: 'Team & Zuweisung', short: 'Team', count: 0, on: pathname.startsWith('/kanzlei/team') },
          {
            to: '/kanzlei/protokoll',
            label: 'Zugriffsprotokoll',
            count: 0,
            on: pathname.startsWith('/kanzlei/protokoll'),
            wide: true,
          },
        ]
      : []),
  ];

  return (
    <div className="kz">
      <header className="top">
        <div className="top-in">
          <Link to="/kanzlei" className="logo" aria-label="Elbcons, zur Übersicht" />
          <div className="brand">Kanzlei</div>
          <Glocke />
          <UserMenu abrechnung={abrechnung ?? 0} post={post ?? 0} ausgang={ausgang ?? 0} />
        </div>
      </header>
      <div className="wrap">
        <nav className="side" aria-label="Bereiche">
          {items.map((i) => (
            <Link key={i.to} to={i.to} aria-current={i.on ? 'page' : undefined} className={i.wide ? 'wide-only' : undefined}>
              <Label text={i.label} short={i.short} />
              {i.count > 0 && <span className="cnt">{i.count}</span>}
            </Link>
          ))}
          <div className="role">
            <b>{user.name}</b>
            {roleText(user)}
          </div>
        </nav>
        <main id="main">{children}</main>
      </div>
    </div>
  );
}

// Rolle in der Seitenleiste (Desktop) und im Konto-Menü (am Handy ist die Seitenleiste eine Reiterzeile ohne Rollen-Box)
function roleText(user: { isAdmin: boolean; area: StaffArea | null }) {
  return user.isAdmin ? 'Admin · sieht alle Mandanten' : `${AREA_LABEL[user.area ?? 'fibu']} · sieht nur eigene Mandanten`;
}

// abrechnung: Mandanten mit Positionen „zu berechnen“, post: Briefe im Posteingang, ausgang: Briefe im Postausgang (am
// Handy und Tablet stehen „Posteingang“, „Postausgang“ und „Abrechnung“ nur hier, nicht in der Reiterzeile)
function UserMenu({ abrechnung, post, ausgang }: { abrechnung: number; post: number; ausgang: number }) {
  const { me } = useKanzlei();
  const { logout } = useSession();
  // Push im Browser am PC (bzw. iPhone vom Home-Bildschirm), solange noch nicht gefragt
  const push = useWebPushState();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);
  return (
    <div className="who" ref={ref}>
      <button type="button" className="who-btn" aria-expanded={open} aria-label="Konto" onClick={() => setOpen((o) => !o)}>
        <span className="lbl">{me.user.name}</span>
        <i>{initials(me.user.name)}</i>
      </button>
      {open && (
        <div className="who-menu" role="menu">
          <b>{me.user.name}</b>
          <small>{me.user.email}</small>
          <small className="who-role">{roleText(me.user)}</small>
          {me.user.isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/kanzlei/posteingang');
              }}
            >
              {post > 0 ? `Posteingang (${post})` : 'Posteingang'}
            </button>
          )}
          {me.user.isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/kanzlei/postausgang');
              }}
            >
              {ausgang > 0 ? `Postausgang (${ausgang})` : 'Postausgang'}
            </button>
          )}
          {me.user.isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/kanzlei/abrechnung');
              }}
            >
              {abrechnung > 0 ? `Abrechnung (${abrechnung})` : 'Abrechnung'}
            </button>
          )}
          {me.user.isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/kanzlei/protokoll');
              }}
            >
              Zugriffsprotokoll
            </button>
          )}
          {push === 'ask' && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void enableWebPush().then((r) =>
                  toast(
                    r === 'on'
                      ? 'Push-Nachrichten sind eingeschaltet.'
                      : r === 'denied'
                        ? 'Ihr Browser blockiert Push-Nachrichten. Sie können sie in den Einstellungen des Browsers für diese Seite erlauben.'
                        : 'Dieser Browser kann keine Push-Nachrichten empfangen. Neues sehen Sie weiter an der Glocke.',
                  ),
                );
              }}
            >
              Push-Nachrichten einschalten
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            Abmelden
          </button>
          <LegalLinks menu />
        </div>
      )}
    </div>
  );
}
